import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Check, Ear, Home, ListChecks, Mic, RotateCcw, Square, Trophy, Volume2, X } from 'lucide-react';
import './App.css';
import { createVoskEngine, isIOS } from './vosk-engine';
import { chooseSpokenLetters, normalize } from './lib/spelling';
import { diffSpelling } from './lib/spelling-diff';
import SpellingMarkup from './components/SpellingMarkup';
import { filterWords, groupBundles, loadSelection, mergeDefinitions, parseDefinitions, parseWords, saveSelection } from './lib/wordlists';
import { playTakes } from './lib/take-sequence';
import { loadProgress, newSession, noteAsked, noteResult, pickNext, record, resetProgress, saveProgress, summarize } from './lib/scheduler';
export { chooseSpokenLetters, parseSpokenLetters } from './lib/spelling';
export { parseCSVLine } from './lib/wordlists';

// Turn a Vosk start-up failure into a reason and concrete fixes for the banner.
export const describeEngineFailure = (error) => {
  const message = `${error?.message || ''} ${error?.cause?.message || ''}`.toLowerCase();
  const insecure = typeof window !== 'undefined' && window.location.protocol === 'http:' && !['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (insecure || message.includes('microphone audio apis')) return {
    reason: 'This page cannot open the microphone audio pipeline.',
    tips: ['Open the app over https:// (or on localhost) — browsers block microphone audio on plain http.',
      'Use a current Chrome, Edge, Firefox or Safari.']
  };
  if (message.includes('permission') || message.includes('notallowed') || message.includes('denied')) return {
    reason: 'Microphone access was denied.',
    tips: ['Click the lock/site icon in the address bar and allow the microphone, then retry.',
      'On macOS also check System Settings → Privacy & Security → Microphone for your browser.']
  };
  if (message.includes('microphone was closed')) return {
    reason: 'The microphone was closed while the app was in the background.',
    tips: ['Press "Retry on-device recognition" to reopen it.', 'Keep the app in the foreground during a session.']
  };
  if (message.includes('model') || message.includes('fetch') || message.includes('network') || message.includes('load')) return {
    reason: 'The 39 MB offline speech model could not be downloaded.',
    tips: ['Check your internet connection — the model is fetched once, then cached by the browser.',
      'If you are on a locked-down network, the model host (ccoreilly.github.io) may be blocked; try another network.',
      'Reload the page and retry; a partial download can leave a broken cache entry.']
  };
  return {
    reason: 'The offline recognizer failed to start.',
    tips: ['Reload the page and retry.', 'Use a current Chrome, Edge, Firefox or Safari — WebAssembly and Web Workers are required.',
      'Open the browser console (F12) for the underlying error.']
  };
};

// Prefer a natural *local* English voice. Network voices (Chrome's "Google US
// English") can hang silently — no onstart, no sound — and wedge the synthesizer
// for the rest of the page, so they are never chosen. Voices load asynchronously
// in Chrome, hence re-evaluated on every call.
const PREFERRED_VOICES = ['Samantha', 'Ava', 'Allison', 'Daniel', 'Karen', 'Moira', 'Microsoft Zira', 'Microsoft David'];
const pickVoice = () => {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const local = voices.filter(v => v.localService && /^en[-_]/i.test(v.lang));
  return PREFERRED_VOICES.map(name => local.find(v => v.name.startsWith(name))).find(Boolean) || local[0] || null;
};
// getVoices() is empty until Chrome has loaded the list; speaking before then
// falls back to Chrome's default — the network voice above. Warm the list on
// load and let the first utterance wait (briefly) for it.
const voicesReady = new Promise(resolve => {
  const synth = window.speechSynthesis;
  if (!synth) return resolve();
  if (synth.getVoices().length) return resolve();
  const done = () => { synth.removeEventListener?.('voiceschanged', done); resolve(); };
  synth.addEventListener?.('voiceschanged', done);
  setTimeout(done, 1500);
});

// Does a recording exist? Checked up front because an <audio> pointed at a
// missing file can hang forever with neither an error event nor a rejected
// play() (the dev server answers 404s with index.html), so waiting on events
// alone left unrecorded words silent.
const recordingCache = new Map();
const hasRecording = (url) => {
  if (!recordingCache.has(url)) {
    recordingCache.set(url, fetch(url, { method: 'HEAD' })
      .then(r => r.ok && /audio|octet-stream/i.test(r.headers.get('content-type') || ''))
      .catch(() => false));
  }
  return recordingCache.get(url);
};

// iOS Safari only lets a media element or the speech synthesizer make a sound
// once it has been used inside a tap. Word clips arrive seconds later (after
// the model loads, after the previous word ends), so the Start tap primes both:
// one shared <audio> element played silently (a fresh `new Audio()` per word
// would each need its own tap) and an empty utterance for the synthesizer.
const SILENT_WAV = 'data:audio/wav;base64,UklGRkQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YSAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==';
const promptAudio = typeof Audio !== 'undefined' ? new Audio() : null;
if (promptAudio) { promptAudio.preload = 'auto'; promptAudio.setAttribute('playsinline', ''); }
const unlockAudio = () => {
  if (promptAudio) {
    promptAudio.src = SILENT_WAV;
    promptAudio.play().catch(() => {});
  }
  const synth = window.speechSynthesis;
  if (synth && isIOS()) { try { synth.speak(new SpeechSynthesisUtterance('')); } catch (_) {} }
};

// Pause before a spelling is auto-submitted. Long by default so a merged or
// missed letter can be noticed and re-said; short once the spelling already
// matches, so a correct answer is not left hanging.
const AUTO_SUBMIT_MS = 5000;
const AUTO_SUBMIT_CORRECT_MS = 1500;
// How long the correction screen stays up before the next word. Also drives the countdown bar.
const REVIEW_MS = 5000;

if (typeof window !== 'undefined') window.speechSynthesis?.getVoices?.(); // kicks off voice loading in Chrome

function App() {
  const [allWords, setAllWords] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [listsOpen, setListsOpen] = useState(false);
  const words = React.useMemo(() => filterWords(allWords, selectedLists), [allWords, selectedLists]);
  const listGroups = React.useMemo(() => groupBundles(allWords), [allWords]);
  const [phase, setPhase] = useState('home');
  const [current, setCurrent] = useState(null);
  const [spelling, setSpelling] = useState('');
  const [status, setStatus] = useState('Ready');
  const [mode, setMode] = useState('idle'); // speaking | listening | idle
  const [definition, setDefinition] = useState(false);
  const [result, setResult] = useState(null);
  const [correction, setCorrection] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [missed, setMissed] = useState([]);
  const [progress, setProgress] = useState(loadProgress);
  const [everything, setEverything] = useState(false); // mock bee: ignore the schedule, still record results
  const [promoted, setPromoted] = useState({ up: 0, down: 0 });
  const [reason, setReason] = useState(null); // why the current word was chosen: learning | due | new | ahead | random
  const [supported, setSupported] = useState(true);
  const [engineState, setEngineState] = useState('idle');
  const [engineFailure, setEngineFailure] = useState(null);
  const engineRef = useRef(null);
  const handleTranscriptRef = useRef(null);
  const submitRef = useRef(null);
  const recognitionRef = useRef(null);
  const activeRef = useRef(false);
  const spellingRef = useRef('');
  const currentRef = useRef(null);
  const wordsRef = useRef([]);
  const timerRef = useRef(null);
  const reviewTimerRef = useRef(null);
  const audioRef = useRef(null);
  const playTokenRef = useRef(null); // identifies the prompt currently allowed to start listening
  const rejectedRef = useRef(false);
  const submittingRef = useRef(false);
  const gradedRef = useRef(false); // true from grading until the next word starts
  const progressRef = useRef(progress);
  const sessionRef = useRef(newSession());
  const everythingRef = useRef(false);

  useEffect(() => { spellingRef.current = spelling; }, [spelling]);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { wordsRef.current = words; }, [words]);
  useEffect(() => { progressRef.current = progress; saveProgress(progress); }, [progress]);
  useEffect(() => { everythingRef.current = everything; }, [everything]);
  const summary = React.useMemo(() => summarize(words, progress), [words, progress]);
  useEffect(() => {
    const definitions = fetch('definitions.csv').then(r => (r.ok ? r.text() : '')).catch(() => '');
    Promise.all([fetch('words.csv').then(r => r.text()), definitions]).then(([text, defs]) => {
      const parsed = mergeDefinitions(parseWords(text), parseDefinitions(defs));
      setAllWords(parsed);
      setSelectedLists(loadSelection(parsed));
    });
  }, []);

  const updateLists = useCallback((next) => { setSelectedLists(next); saveSelection(next); }, []);
  const toggleList = (name) => updateLists(selectedLists.includes(name) ? selectedLists.filter(n => n !== name) : [...selectedLists, name]);
  const setGroup = (group, on) => {
    const names = group.bundles.map(b => b.name);
    updateLists(on ? [...new Set([...selectedLists, ...names])] : selectedLists.filter(n => !names.includes(n)));
  };
  const resetGroup = (group) => {
    const names = new Set(group.bundles.map(b => b.name));
    setProgress(p => resetProgress(p, allWords.filter(w => w.bundles.some(b => names.has(b))).map(w => w.id)));
  };
  const resetAll = () => setProgress(resetProgress(progress));

  const stopRecognition = useCallback(() => {
    engineRef.current?.stop();
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.onresult = null; // late results must not reach the next word
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
  }, []);

  const speak = useCallback((text, onDone) => {
    stopRecognition();
    setMode('speaking');
    window.speechSynthesis?.cancel();
    // Chrome sometimes never fires onend (notably right after cancel()), which
    // would leave the session stuck; a watchdog sized to the text fires instead.
    let finished = false;
    const finish = () => { if (finished) return; finished = true; clearTimeout(watchdog); onDone?.(); };
    const watchdog = setTimeout(() => { window.speechSynthesis?.cancel(); finish(); }, 3000 + text.length * 90);
    voicesReady.then(() => {
      if (finished) return;
      const utterance = new SpeechSynthesisUtterance(text);
      const voice = pickVoice();
      if (voice) { utterance.voice = voice; utterance.lang = voice.lang; }
      else utterance.lang = 'en-US';
      utterance.rate = 0.9;
      utterance.onend = finish;
      utterance.onerror = finish;
      window.speechSynthesis?.speak(utterance);
    });
  }, [stopRecognition]);

  // `both`: also play the word's second take, a second after the first (Repeat does; the first play of a word does not).
  const playPrompt = useCallback((word, after, { both = false } = {}) => {
    clearTimeout(timerRef.current);
    stopRecognition();
    setMode('speaking');
    setStatus('Listen to the word');
    const url = `/recordings/${word.id}.mp3`; // id-keyed: words may repeat or contain spaces
    // Fire `after` once only, and never once the word has been graded (a late
    // "audio ended" must not restart listening over the Correct/Not quite screen).
    const token = {};
    playTokenRef.current = token;
    let done = false;
    const current = () => playTokenRef.current === token && !gradedRef.current;
    const once = () => { if (done || !current()) return; done = true; after?.(); };
    let fellBack = false;
    let stall;
    const fallback = () => {
      if (fellBack || !current()) return;
      fellBack = true; clearTimeout(stall);
      engineRef.current?.stopClip(); promptAudio?.pause();
      speak(word.word, once);
    };
    hasRecording(url).then(exists => {
      if (!current()) return;
      if (!exists) return fallback();
      stall = setTimeout(fallback, 2500); // never wait forever on a clip that will not start
      const play = (clipUrl, onStart) => {
        const engine = engineRef.current;
        // Through the recognizer's own audio context: see playClip in vosk-engine.
        if (engine?.playClip) return engine.playClip(clipUrl, onStart);
        return new Promise((resolve, reject) => {
          const audio = promptAudio || new Audio();
          audioRef.current = audio;
          audio.src = clipUrl;
          audio.onended = resolve;
          audio.onerror = reject;
          audio.onplaying = onStart;
          audio.play().catch(reject);
        });
      };
      playTakes({
        play, url, exists: hasRecording, wanted: () => current() && activeRef.current, // Done during the pause must not bring back the second take
        secondUrl: both ? `/recordings/${word.id}-2.mp3` : null,
        preload: (clipUrl) => engineRef.current?.preloadClip?.(clipUrl),
        onStart: () => clearTimeout(stall),
      }).then(once, fallback);
    });
  }, [speak, stopRecognition]);

  const chooseWord = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(reviewTimerRef.current);
    const list = wordsRef.current;
    if (!list.length) return;
    const pick = pickNext(list, progressRef.current, sessionRef.current, { mode: everythingRef.current ? 'everything' : 'spaced' });
    if (!pick) return;
    const next = pick.word;
    sessionRef.current = noteAsked(sessionRef.current, next.id);
    setReason(pick.reason);
    currentRef.current = next; setCurrent(next); setSpelling(''); spellingRef.current = ''; rejectedRef.current = false; gradedRef.current = false;
    setDefinition(false); setResult(null); setCorrection('');
    setTimeout(() => playPrompt(next, () => beginListeningRef.current?.()), 250);
  }, [playPrompt]);

  const stopEverything = useCallback(() => {
    activeRef.current = false; clearTimeout(timerRef.current); clearTimeout(reviewTimerRef.current); stopRecognition(); window.speechSynthesis?.cancel();
    audioRef.current?.pause(); engineRef.current?.stopClip(); setMode('idle');
  }, [stopRecognition]);

  const finish = useCallback(() => {
    stopEverything(); setPhase('finished'); setStatus('Session complete');
  }, [stopEverything]);

  // Back to the home screen — where the word lists can be changed before another session.
  const goHome = useCallback(() => {
    stopEverything(); setPhase('home'); setCurrent(null); setCorrection(''); setResult(null);
    setSpelling(''); spellingRef.current = ''; setStatus('Ready');
  }, [stopEverything]);

  // The single place a graded attempt reaches the schedule and the session record.
  const grade = useCallback((correct) => {
    const word = currentRef.current; if (!word) return;
    const before = progressRef.current.words[word.id]?.step || 0;
    const next = record(progressRef.current, word.id, correct);
    progressRef.current = next; setProgress(next);
    sessionRef.current = noteResult(sessionRef.current, word.id, correct);
    const after = next.words[word.id].step;
    setPromoted(p => ({ up: p.up + (after > before ? 1 : 0), down: p.down + (after < before ? 1 : 0) }));
  }, []);

  const submit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    clearTimeout(timerRef.current);
    if (engineRef.current) {
      // On slow devices the decoder lags real time: wait for it to catch up so
      // the last letters spoken are graded rather than lost in the queue.
      const slow = setTimeout(() => setStatus('Catching up…'), 400);
      await engineRef.current.flush();
      clearTimeout(slow);
    }
    stopRecognition(); setMode('idle');
    submittingRef.current = false;
    const answer = normalize(spellingRef.current);
    const accepted = currentRef.current?.accepted || [normalize(currentRef.current?.word)];
    if (!activeRef.current) return;
    if (!answer) { setStatus('Say the letters, then pause or press Done'); setTimeout(() => beginListeningRef.current?.(), 300); return; }
    const correct = accepted.includes(answer); // primary, alternate and allowable spellings all count
    gradedRef.current = true; if (audioRef.current) audioRef.current.onended = null;
    setScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    if (!correct) setMissed(items => [...items, currentRef.current.word]);
    grade(correct);
    setResult(correct ? 'correct' : 'incorrect');
    if (correct) {
      speak('Correct.', () => setTimeout(chooseWord, 500));
    } else {
      setCorrection(currentRef.current.word);
      speak('Not quite. Study the correct spelling.');
      reviewTimerRef.current = setTimeout(() => {
        window.speechSynthesis?.cancel();
        chooseWord();
      }, REVIEW_MS);
    }
  }, [chooseWord, grade, speak, stopRecognition]);
  useEffect(() => { submitRef.current = submit; }, [submit]);

  const reviewThenNext = useCallback((message) => {
    stopRecognition(); gradedRef.current = true;
    setCorrection(currentRef.current.word);
    speak(message);
    reviewTimerRef.current = setTimeout(() => {
      window.speechSynthesis?.cancel();
      chooseWord();
    }, REVIEW_MS);
  }, [chooseWord, speak, stopRecognition]);
  useEffect(() => { if (correction) setMode('idle'); }, [correction]);

  const repeatWord = useCallback(() => {
    if (!currentRef.current || correction) return;
    playPrompt(currentRef.current, () => beginListeningRef.current?.(), { both: true });
  }, [correction, playPrompt]);

  const showDefinition = useCallback(() => {
    if (!currentRef.current || correction) return;
    setDefinition(true);
    const { partOfSpeech, definition: text } = currentRef.current;
    speak(partOfSpeech ? `${partOfSpeech}. ${text}` : text, () => beginListeningRef.current?.());
  }, [correction, speak]);

  const clearSpelling = useCallback(() => {
    clearTimeout(timerRef.current);
    rejectedRef.current = false;
    spellingRef.current = ''; setSpelling(''); setStatus('Starting over — spell it again.');
  }, []);

  const skipWord = useCallback(() => {
    if (!currentRef.current || correction) return;
    clearTimeout(timerRef.current);
    setScore(s => ({ ...s, total: s.total + 1 }));
    setMissed(items => [...items, currentRef.current.word]);
    grade(false);
    spellingRef.current = ''; setSpelling(''); setResult('incorrect');
    reviewThenNext('Skipped. Study the correct spelling.');
  }, [correction, grade, reviewThenNext]);

  const scheduleAutoSubmit = useCallback(() => {
    clearTimeout(timerRef.current);
    const done = (currentRef.current?.accepted || []).includes(normalize(spellingRef.current));
    timerRef.current = setTimeout(() => submitRef.current?.(), done ? AUTO_SUBMIT_CORRECT_MS : AUTO_SUBMIT_MS);
  }, []);

  const handleTranscript = useCallback((alternatives) => {
    const transcripts = alternatives.map(t => t.toLowerCase().trim()).filter(Boolean);
    if (!transcripts.length) return;
    const prefix = normalize(spellingRef.current);
    const expected = normalize(currentRef.current?.word);
    const letters = chooseSpokenLetters(transcripts, prefix, expected);
    if (!letters) {
      // Never let a missed letter slip through silently: block auto-submit
      // until the next accepted letter or an explicit Done.
      clearTimeout(timerRef.current);
      rejectedRef.current = true;
      setStatus(`I heard “${alternatives[0]}” — say that letter again`);
      return;
    }
    rejectedRef.current = false;
    spellingRef.current += letters; setSpelling(spellingRef.current);
    setStatus('Keep spelling, or pause to submit');
    if (submittingRef.current) return; // letter arrived while draining: graded by the pending submit
    clearTimeout(reviewTimerRef.current);
    scheduleAutoSubmit();
  }, [scheduleAutoSubmit]);

  const beginListening = useCallback(() => {
    if (!activeRef.current || gradedRef.current) return;
    if (engineRef.current) {
      engineRef.current.start();
      setMode('listening');
      setStatus(spellingRef.current ? 'Listening for the next letter' : 'Spell one letter at a time');
      return;
    }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { setSupported(false); setStatus('Voice recognition needs Chrome or Edge'); return; }
    stopRecognition();
    const recognition = new Recognition(); recognition.lang = 'en-US'; recognition.continuous = true;
    recognition.interimResults = false; recognition.maxAlternatives = 8;
    recognition.onstart = () => setMode('listening') || setStatus(spellingRef.current ? 'Listening for the next letter' : 'Spell the word');
    recognition.onresult = event => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (!event.results[i].isFinal) continue;
        const alternatives = Array.from(event.results[i]).map(item => item.transcript);
        handleTranscript(alternatives);
      }
    };
    recognition.onerror = event => { if (!['no-speech', 'aborted'].includes(event.error)) setStatus('I missed that. Try again.'); };
    recognition.onend = () => { recognitionRef.current = null; if (activeRef.current) setTimeout(() => beginListeningRef.current?.(), 650); };
    recognitionRef.current = recognition;
    try { recognition.start(); } catch (_) {}
  }, [handleTranscript, stopRecognition]);
  const beginListeningRef = useRef(beginListening);
  useEffect(() => { beginListeningRef.current = beginListening; }, [beginListening]);
  useEffect(() => { handleTranscriptRef.current = handleTranscript; }, [handleTranscript]);

  useEffect(() => () => {
    activeRef.current = false;
    clearTimeout(timerRef.current);
    clearTimeout(reviewTimerRef.current);
    stopRecognition();
    engineRef.current?.destroy();
    window.speechSynthesis?.cancel();
  }, [stopRecognition]);

  const loadOfflineEngine = useCallback(async () => {
    setEngineState('loading');
    try {
      const engine = await createVoskEngine({
        onResult: text => handleTranscriptRef.current?.([text]),
        onPartial: message => setStatus(message),
        // Auto-submit is scheduled per accepted letter (scheduleAutoSubmit);
        // silence only matters when the last utterance was rejected.
        onSilence: () => {
          if (rejectedRef.current) { clearTimeout(timerRef.current); setStatus('Say that letter again, or press Done'); }
        },
        // iOS drops the capture track after the app sits in the background;
        // surface the banner so the Retry button can rebuild the engine.
        onLost: () => {
          engineRef.current?.destroy(); engineRef.current = null;
          setEngineState('browser');
          setEngineFailure(describeEngineFailure(new Error('microphone was closed')));
          if (activeRef.current) { stopRecognition(); setMode('idle'); setStatus('The microphone was closed — press Retry'); }
        }
      });
      engineRef.current = engine;
      setEngineState('vosk');
      setEngineFailure(null);
      return true;
    } catch (error) {
      console.error('Vosk initialization failed', error);
      setEngineState('browser');
      setEngineFailure(describeEngineFailure(error));
      return false;
    }
  }, [stopRecognition]);

  const start = async () => {
    if (engineState === 'loading') return;
    unlockAudio(); // synchronously, while still inside the tap
    if (!engineRef.current && engineState !== 'browser') await loadOfflineEngine();
    activeRef.current = true; gradedRef.current = false; setPhase('playing'); setScore({ correct: 0, total: 0 }); setMissed([]);
    setPromoted({ up: 0, down: 0 }); sessionRef.current = newSession();
    chooseWord();
  };

  const retryOfflineEngine = async () => {
    if (engineState === 'loading') return;
    const wasListening = activeRef.current && !!recognitionRef.current;
    stopRecognition();
    const ok = await loadOfflineEngine();
    if (activeRef.current && (ok || wasListening)) beginListeningRef.current?.();
  };
  const accuracy = score.total ? Math.round(score.correct / score.total * 100) : 0;
  // The red-pen markup of a wrong attempt. A skipped word has no attempt, so nothing to mark.
  const marks = React.useMemo(() => {
    const segments = correction && normalize(spelling) ? diffSpelling(spelling, correction) : [];
    return segments.some(s => s.type === 'change') ? segments : null;
  }, [correction, spelling]);
  return <main className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark"><Ear size={21}/></span><span>Hear & Spell</span></div>
      {phase === 'playing' && <button className="icon-command" onClick={finish} title="Stop session"><Square size={18}/><span>Stop</span></button>}
      {phase === 'finished' && <button className="icon-command" onClick={goHome} title="Back to the home screen"><Home size={18}/><span>Home</span></button>}
    </header>

    {engineState === 'browser' && <aside className="engine-banner" role="alert" aria-live="assertive">
      <div className="engine-banner-head"><AlertTriangle size={18}/><strong>Degraded mode: on-device recognition is off.</strong>
        <span>Using your browser's speech service instead — less accurate for single letters, and audio leaves this device.</span></div>
      <p className="engine-banner-reason">{engineFailure?.reason}</p>
      <ul>{engineFailure?.tips.map(tip => <li key={tip}>{tip}</li>)}</ul>
      <button className="icon-command" onClick={retryOfflineEngine} disabled={engineState === 'loading'}><RotateCcw size={16}/><span>Retry on-device recognition</span></button>
    </aside>}

    {phase === 'home' && <section className="home-view">
      <div className="home-copy"><p className="eyebrow">REAL SPELLING BEE PRACTICE</p><h1>Hear it.<br/>Spell it.<br/><em>Own it.</em></h1>
        <p className="intro">A hands-free practice room built for focused listening and confident spelling.</p>
        <button className="primary-action" onClick={start} disabled={!words.length || engineState === 'loading'}><Mic size={22}/>{engineState === 'loading' ? 'Loading offline speech model…' : allWords.length ? (words.length ? 'Start voice session' : 'Choose a word list') : 'Loading words…'}</button>
        {allWords.length > 0 && <div className="word-lists">
          <button type="button" className="word-lists-summary" onClick={() => setListsOpen(open => !open)} aria-expanded={listsOpen}>
            <ListChecks size={16}/><span><strong>{words.length}</strong> words from <strong>{selectedLists.length}</strong> of {listGroups.reduce((n, g) => n + g.bundles.length, 0)} lists</span><span className="word-lists-toggle">{listsOpen ? 'Hide' : 'Change'}</span>
          </button>
          {words.length > 0 && <div className="progress-line" aria-label="Progress on the chosen lists">
            <span><strong>{summary.due}</strong> due</span><span><strong>{summary.fresh}</strong> new</span><span><strong>{summary.learning}</strong> learning</span><span><strong>{summary.mastered}</strong> mastered</span>
          </div>}
          {listsOpen && <div className="word-lists-panel">
            <label className="everything-toggle"><input type="checkbox" checked={everything} onChange={e => setEverything(e.target.checked)}/>
              <span><strong>Mock bee</strong> — practise everything, ignoring progress (results still count)</span></label>
            {listGroups.map(group => {
              const chosen = group.bundles.filter(b => selectedLists.includes(b.name)).length;
              return <section key={group.title}>
                <header><h3>{group.title}</h3><span>{chosen}/{group.bundles.length}</span>
                  <button type="button" onClick={() => resetGroup(group)} title="Forget progress on these lists">Reset</button>
                  <button type="button" onClick={() => setGroup(group, chosen < group.bundles.length)}>{chosen < group.bundles.length ? 'All' : 'None'}</button></header>
                <div className="word-list-chips">{group.bundles.map(b => <label key={b.name} className={selectedLists.includes(b.name) ? 'on' : ''}>
                  <input type="checkbox" checked={selectedLists.includes(b.name)} onChange={() => toggleList(b.name)}/>{b.label}<small>{b.count}</small></label>)}</div>
              </section>;
            })}
            <footer><button type="button" onClick={resetAll} disabled={!Object.keys(progress.words).length}>Reset all progress</button></footer>
          </div>}
        </div>}
        {engineState === 'loading' && <p className="model-note">First launch downloads a 39 MB model. Later launches use the browser cache.</p>}
        <p className="privacy"><span/> {engineState === 'vosk' ? 'Speech recognition runs on this device' : 'Microphone audio is handled by your browser'}</p></div>
      <div className="bee-stage" aria-hidden="true"><div className="sound-ring ring-one"/><div className="sound-ring ring-two"/><div className="mic-disc"><Mic size={78}/></div><div className="floating-word word-one">listen</div><div className="floating-word word-two">spell</div><div className="floating-word word-three">shine</div></div>
    </section>}

    {phase === 'playing' && <section className="session-view">
      <div className="session-meta"><div><span className="live-dot"/> LIVE SESSION{reason && <em className={`pick-reason ${reason}`}>{{ learning: 'Try again', due: 'Review', new: 'New word', ahead: 'All caught up — early review', random: 'Mock bee' }[reason]}</em>}</div><div className="scoreline"><strong>{score.correct}</strong> correct <span/> <strong>{score.total}</strong> attempted</div></div>
      <div className="prompt-panel">
        {correction && <div className="correction-screen" role="status" aria-live="assertive">
          {marks && <><p className="markup-label">You wrote</p><SpellingMarkup segments={marks}/></>}
          <p>Correct spelling</p>
          <strong>{correction}</strong>
          <div className="review-progress" style={{ '--review-ms': `${REVIEW_MS}ms` }}/>
        </div>}
        <div className={`listen-orb ${mode}`} aria-hidden="true">{mode === 'listening' ? <Mic size={34}/> : <Volume2 size={34}/>}</div>
        <p className="orb-caption">{mode === 'listening' ? 'Your turn — spell it' : mode === 'speaking' ? 'Listen…' : '\u00a0'}</p>
        <p className="status-label">{status}</p>
        <div className={`letter-row ${correction ? 'correction-row' : ''}`} aria-label={`Spelling: ${correction || spelling || 'empty'}`}>
          {(correction || spelling) ? (correction || spelling).split('').map((letter, i, letters) => <span style={{ fontSize: `${Math.max(15, Math.min(38, 390 / letters.length))}px` }} key={`${letter}-${i}`}>{letter.toUpperCase()}</span>) : <div className="waiting-line"/>}
        </div>
        {correction && <p className="correction-label">Correct spelling</p>}
        {result && <div className={`result ${result}`}>{result === 'correct' ? <Check/> : <X/>}{result === 'correct' ? 'Correct' : 'Not quite'}</div>}
        {definition && <div className="definition"><BookOpen size={18}/><div>
          <p>{current?.partOfSpeech && <em>{current.partOfSpeech}</em>}{current?.definition}</p></div></div>}
      </div>
      <div className="voice-guide"><span>Say each letter with a short beat, then pause to submit</span></div>
      <div className="action-row">
        <button className="icon-command" onClick={repeatWord} disabled={!!correction}><Volume2 size={16}/><span>Repeat</span></button>
        {current?.definition && <button className="icon-command" onClick={showDefinition} disabled={!!correction}><BookOpen size={16}/><span>Definition</span></button>}
        <button className={`icon-command start-over ${mode === 'listening' && !correction ? 'prominent' : ''}`} onClick={clearSpelling} disabled={!!correction || (!spelling && mode !== 'listening')} title="Wipe the letters so far and spell the word again"><RotateCcw size={16}/><span>Start over</span></button>
        <button className="icon-command" onClick={skipWord} disabled={!!correction}><X size={16}/><span>Skip</span></button>
        <button className="icon-command done" onClick={submit} disabled={!!correction || !spelling}><Check size={16}/><span>Done</span></button>
      </div>
      {!supported && <p className="browser-warning">Use Chrome or Edge and allow microphone access. Typed fallback: enter letters below.</p>}
      <input className="keyboard-fallback" value={spelling} onChange={e => { spellingRef.current = e.target.value; setSpelling(e.target.value); }} onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Keyboard fallback" aria-label="Keyboard spelling fallback"/>
    </section>}

    {phase === 'finished' && <section className="results-view"><Trophy size={50}/><p className="eyebrow">SESSION COMPLETE</p><h1>{accuracy}%</h1><p>{score.correct} of {score.total} words correct</p>
      {score.total > 0 && <p className="promotion">{promoted.up} {promoted.up === 1 ? 'word' : 'words'} moved up a step · {promoted.down} back to the start · {summary.mastered} mastered of {summary.total}</p>}
      {missed.length > 0 && <div className="review"><h2>Words to review</h2><div>{missed.map((word, i) => <span key={`${word}-${i}`}>{word}</span>)}</div></div>}
      <div className="results-actions">
        <button className="primary-action" onClick={start}><RotateCcw size={20}/>Practice again</button>
        <button className="icon-command" onClick={goHome}><Home size={16}/><span>Change word lists</span></button>
      </div></section>}
  </main>;
}

export default App;
